import { useCallback } from 'react';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type MetricValue } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import {
  createWMSOptimizedTerritories,
  type CreateWMSTerritoriesRequestParams, type TargetMetric,
  type WMSMetricConstraint,
} from '~/store/boundaries/boundaries.repository';

export const useGenerateWMSOptimizedTerritories = () => {
  const { invokeAjax, isLoading } = useAjaxCall((clientId: number, params: CreateWMSTerritoriesRequestParams) =>
    createWMSOptimizedTerritories(clientId, params)
  );

  const createOptimizedTerritoriesRequest = useCallback(({
    clientId,
    dataMetricsValues,
    demographicsValues,
    useConstraints,
    ...restData
  }: {
    clientId: number;
    dataMetricsValues: readonly MetricValue[];
    demographicsValues: readonly MetricValue[];
    useConstraints: boolean;
  } & Omit<CreateWMSTerritoriesRequestParams, 'targetMetrics' | 'constraints'>
  ) => {
    const metrics = [
      ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.checked),
      ...demographicsValues.filter(demographicsValue => demographicsValue.checked),
    ];

    const targetMetrics: TargetMetric[] = metrics.map(metricValue => ({
      name: metricValue.id,
      weighting: metricValue.weight,
    }));

    const constraints = !useConstraints ? undefined : metrics
      .filter(metric => metric.isConstraint)
      .flatMap(metric => [
        {
          metric: metric.id,
          operator: 'gte',
          value: metric.constraintLowerBound,
        }, {
          metric: metric.id,
          operator: 'lte',
          value: metric.constraintUpperBound,
        },
      ])
      .filter((constraint): constraint is WMSMetricConstraint => notNullsy(constraint.value));

    return invokeAjax(clientId, {
      ...restData,
      targetMetrics,
      constraints,
    });
  }, [invokeAjax]);

  return {
    isLoading,
    createOptimizedTerritoriesRequest,
  };
};
